import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button as MaterialButton } from '@mui/material';
import { SimpleText } from '../../util/IntlMessages';

import styles from './styles.module.scss';

export const BUTTON_LABEL_ALIGN = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
};

class BaseButton extends PureComponent {
  static propTypes = {
    labelAlign: PropTypes.oneOf(Object.values(BUTTON_LABEL_ALIGN)),
    labelId: PropTypes.string,
    children: PropTypes.node,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    iconLeft: PropTypes.element,
    iconRight: PropTypes.element,
    iconNoLabel: PropTypes.element,
    variant: PropTypes.string,
    className: PropTypes.string,
    iconClassName: PropTypes.string,
    labelClassName: PropTypes.string,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  static defaultProps = {
    size: 'large',
    labelAlign: BUTTON_LABEL_ALIGN.CENTER,
  };

  handleButtonClick = (...args) => {
    if (this.props.onClick) {
      Object.prototype.hasOwnProperty.call(this.props, 'id')
        ? this.props.onClick(this.props.id, ...args)
        : this.props.onClick(...args, this.props.id);
    }
  };

  render() {
    const {
      labelAlign,
      size,
      labelId,
      iconRight,
      iconLeft,
      iconNoLabel,
      variant,
      className,
      iconClassName,
      labelClassName,
      children,
      disabled,
      forwardedRef,
      ...rest
    } = this.props;

    return (
      <MaterialButton
        ref={forwardedRef}
        {...rest}
        disabled={disabled}
        variant={variant}
        size={size}
        onClick={this.handleButtonClick}
        className={cx(
          styles.button,
          { [styles.nolabel]: !!iconNoLabel },
          { [styles.disabled]: disabled },
          className,
        )}
      >
        {iconLeft && (
          <span className={cx(styles.icon, styles.left, iconClassName)}>{iconLeft}</span>
        )}
        {iconNoLabel && (
          <span className={cx(styles.icon, styles.noLabel, iconClassName)}>{iconNoLabel}</span>
        )}
        <span
          className={cx(styles.label, { [labelClassName]: labelClassName })}
          style={{ textAlign: labelAlign }}
        >
          {labelId && <SimpleText id={labelId} />}
          {children && children}
        </span>
        {iconRight && (
          <span className={cx(styles.icon, styles.right, iconClassName)}>{iconRight}</span>
        )}
      </MaterialButton>
    );
  }
}

// forwardRef so MUI Tooltip (and any ref-attaching parent) can resolve a DOM
// node — MUI 7 Tooltip's mount effect calls childNode.getAttribute(), which
// throws on a non-ref-forwarding class child and unmounts the SPA.
BaseButton.propTypes.forwardedRef = PropTypes.oneOfType([PropTypes.func, PropTypes.object]);

export const Button = React.forwardRef((props, ref) => (
  <BaseButton {...props} forwardedRef={ref} />
));
Button.displayName = 'Button';
